import React from 'react';
import {
  Box, Chip, IconButton, Stack, Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import {
  CATEGORY_OPTIONS, findOption, fmtDateTime,
} from '../../views/Humen/announcement/constants';
import PdfViewer from './PdfViewer';

import { brandHex } from '../../utils/orgConfig';
// แยกชนิดไฟล์แนบจากนามสกุลใน url (ไฟล์จาก /upload/file เก็บนามสกุลเดิมไว้เสมอ)
const KIND_RE = [
  ['image', /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i],
  ['pdf', /\.pdf(\?|$)/i],
  ['frame', /\.txt(\?|$)/i],
  ['video', /\.(mp4|webm|mov|m4v)(\?|$)/i],
  ['audio', /\.(mp3|wav|m4a|aac|ogg)(\?|$)/i],
  ['office', /\.(docx?|xlsx?|pptx?)(\?|$)/i],
];
const kindOf = (url) => (KIND_RE.find(([, re]) => re.test(url)) || ['file'])[0];

// ไฟล์ที่แสดงเป็นกรอบใหญ่ — dialog ควรขยายกว้างขึ้นให้อ่านง่าย
const WIDE_KINDS = ['pdf', 'frame', 'video', 'office'];
export const hasWideAttachment = (announcement) => (announcement?.attachments || [])
  .some((a) => WIDE_KINDS.includes(kindOf(a.url)));

// สีส่วนหัวตามระดับความสำคัญ
export const PRIORITY_THEME = {
  normal: { label: 'ประกาศ', gradient: 'linear-gradient(135deg, rgb(var(--color-primary-450)) 0%, rgb(var(--color-primary-500)) 55%, rgb(var(--color-primary-600)) 100%)', get color() { return brandHex(500); } },
  important: { label: 'ประกาศสำคัญ', gradient: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)', color: '#f59e0b' },
  critical: { label: 'ประกาศด่วน', gradient: 'linear-gradient(135deg, #f87171 0%, #ef4444 50%, #b91c1c 100%)', color: '#ef4444' },
};
export const priorityTheme = (p) => PRIORITY_THEME[p] || PRIORITY_THEME.normal;

const FRAME_SX = {
  width: '100%',
  height: { xs: '60vh', md: '70vh' },
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  display: 'block',
};

function OpenLink({ attachment }) {
  return (
    <Typography
      variant="caption"
      component="a"
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      sx={{ display: 'inline-block', mt: 0.5, color: 'primary.main' }}
    >
      {attachment.name || 'ไฟล์แนบ'} — เปิดในหน้าต่างใหม่
    </Typography>
  );
}

// แสดงไฟล์แนบในประกาศเลยโดยไม่ต้องกดเปิด — ไฟล์ที่เบราว์เซอร์แสดงไม่ได้ (zip ฯลฯ) ถึงจะเป็นปุ่มให้กดเปิดแทน
function AttachmentPreview({ attachment: a, announcementId }) {
  const kind = kindOf(a.url);

  if (kind === 'image') {
    return (
      <a href={a.url} target="_blank" rel="noreferrer">
        <Box
          component="img"
          src={a.url}
          alt={a.name}
          sx={{
            maxWidth: '100%', borderRadius: 2, display: 'block', boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          }}
        />
      </a>
    );
  }
  if (kind === 'video') {
    return (
      <Box>
        <Box component="video" src={a.url} controls preload="metadata" sx={{ width: '100%', maxHeight: '70vh', borderRadius: 2, bgcolor: '#000' }} />
        <OpenLink attachment={a} />
      </Box>
    );
  }
  if (kind === 'audio') {
    return (
      <Box sx={{
        p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
      }}
      >
        <Typography variant="caption" color="text.secondary">{a.name}</Typography>
        <Box component="audio" src={a.url} controls preload="metadata" sx={{ width: '100%', display: 'block' }} />
      </Box>
    );
  }
  // PDF วาดเองด้วย pdf.js ให้เหมือนกันทุก OS (ดู PdfViewer.js)
  if (kind === 'pdf') {
    return <PdfViewer announcementId={announcementId} url={a.url} name={a.name} />;
  }
  // txt ใช้ตัวดูของเบราว์เซอร์ (แสดงข้อความธรรมดาได้เหมือนกันทุก OS)
  if (kind === 'frame') {
    return (
      <Box>
        <Box component="iframe" src={a.url} title={a.name || 'ไฟล์แนบ'} sx={FRAME_SX} />
        <OpenLink attachment={a} />
      </Box>
    );
  }
  // Word/Excel/PowerPoint แสดงผ่าน Microsoft Office Online viewer (ใช้ได้เพราะไฟล์บน GCS เป็น public url อยู่แล้ว)
  if (kind === 'office') {
    return (
      <Box>
        <Box
          component="iframe"
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(a.url)}`}
          title={a.name || 'ไฟล์แนบ'}
          sx={FRAME_SX}
        />
        <OpenLink attachment={a} />
      </Box>
    );
  }

  return null;
}

// ส่วนหัวใหญ่ของประกาศ (ไล่สีตามระดับ) — ใช้ใน popup และตอนเปิดอ่านจากรายการประกาศ
export function AnnouncementHero({
  announcement, counter, onClose, onBack,
}) {
  const pt = priorityTheme(announcement.priority);
  const cat = findOption(CATEGORY_OPTIONS, announcement.category);
  const whiteChip = {
    bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, backdropFilter: 'blur(4px)',
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: pt.gradient,
        color: '#fff',
        px: 3,
        pt: 2.25,
        pb: 2.75,
      }}
    >
      {/* วงกลมตกแต่งมุมขวา */}
      <Box sx={{
        position: 'absolute', right: -40, top: -50, width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)',
      }}
      />
      <Box sx={{
        position: 'absolute', right: 40, bottom: -70, width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)',
      }}
      />

      <Stack direction="row" alignItems="center" spacing={1} sx={{ position: 'relative' }}>
        {onBack}
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'rgba(255,255,255,0.22)',
          }}
        >
          <CampaignIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography fontWeight={700} fontSize="0.9rem" sx={{ letterSpacing: 0.3 }}>{pt.label}</Typography>
        <Box flexGrow={1} />
        {counter && (
          <Box sx={{
            px: 1.25, py: 0.25, borderRadius: 5, bgcolor: 'rgba(0,0,0,0.18)', fontSize: '0.75rem', fontWeight: 600,
          }}
          >
            {counter}
          </Box>
        )}
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mt: 2, position: 'relative' }}>
        <Chip size="small" label={cat.label} sx={whiteChip} />
        {announcement.pinned && (
          <Chip size="small" icon={<PushPinIcon sx={{ fontSize: 14, color: '#fff !important' }} />} label="ปักหมุด" sx={whiteChip} />
        )}
      </Stack>
      <Typography
        sx={{
          mt: 1.25, fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.4rem' }, lineHeight: 1.35, position: 'relative', wordBreak: 'break-word',
        }}
      >
        {announcement.title}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.85, position: 'relative' }}>
        {announcement.created_by_name ? `${announcement.created_by_name} · ` : ''}
        {fmtDateTime(announcement.createdAt)}
      </Typography>
    </Box>
  );
}

// เนื้อหาประกาศ (ข้อความ + ไฟล์แนบ) — ส่วนหัวแยกไปอยู่ที่ AnnouncementHero
export default function AnnouncementContent({ announcement }) {
  const attachments = announcement.attachments || [];
  const previews = attachments.filter((a) => kindOf(a.url) !== 'file');
  const files = attachments.filter((a) => kindOf(a.url) === 'file');

  return (
    <Box>
      {announcement.body ? (
        <Typography sx={{
          fontSize: '0.95rem', lineHeight: 1.85, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary',
        }}
        >
          {announcement.body}
        </Typography>
      ) : !attachments.length && (
        <Typography color="text.secondary" fontStyle="italic">ไม่มีรายละเอียดเพิ่มเติม</Typography>
      )}

      {attachments.length > 0 && (
        <Box sx={{ mt: announcement.body ? 3 : 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
            <AttachFileIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              {`ไฟล์แนบ (${attachments.length})`}
            </Typography>
          </Stack>
          {previews.length > 0 && (
            <Stack spacing={2}>
              {previews.map((a) => <AttachmentPreview key={a.url} attachment={a} announcementId={announcement.id} />)}
            </Stack>
          )}
          {files.length > 0 && (
            <Stack direction="row" sx={{ mt: previews.length ? 2 : 0, flexWrap: 'wrap', gap: 1 }}>
              {files.map((a, i) => (
                <Chip
                  key={a.url}
                  icon={<AttachFileIcon />}
                  label={a.name || `ไฟล์ ${i + 1}`}
                  component="a"
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  clickable
                  variant="outlined"
                  sx={{ borderColor: 'rgb(var(--color-primary-500) / 0.3)' }}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}
